import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { useIsAdmin } from "../lib/currentUser";
import { requireAdmin } from "../lib/requireAuth";
import { FilterSelect } from "../components/FilterSelect";
import { FilterBar, FilterEmpty } from "../components/FilterBar";
import { SearchInput } from "../components/SearchInput";
import { PageEyebrow } from "../components/PageEyebrow";

export const Route = createFileRoute("/admin/reports")({
  beforeLoad: ({ context, location }) =>
    requireAdmin(context.queryClient, location.href),
  component: AdminReportsPage,
});

const CATEGORY_LABEL: Record<string, string> = {
  wrong_answer: "Wrong answer",
  unclear: "Unclear",
  broken: "Broken",
  disliked: "Disliked",
  other: "Other",
};

/**
 * Admin-facing list of exercise reports — the queue the curriculum
 * maintainer triages when learners flag broken / unclear / wrong
 * exercises. Phase 1G ships read-only; resolution still happens via
 * direct SQL or content edits.
 */
function AdminReportsPage() {
  const isAdmin = useIsAdmin();
  const [category, setCategory] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const reportsQ = useQuery({
    queryKey: ["admin-reports"],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/reports");
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <section className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center">
        <div className="eyebrow mb-3">Admin</div>
        <h1 className="font-serif text-3xl text-ink-900 mb-4 tracking-tight">
          Admins only
        </h1>
        <p className="text-ink-500 mb-8">
          This page is restricted to users with the{" "}
          <code className="font-mono text-ink-700 bg-ink-50 border border-ink-100 px-1.5 py-0.5">
            admin
          </code>{" "}
          role.
        </p>
        <Link
          to="/home"
          className="inline-block border border-ink-200 px-5 py-3 text-ink-700 hover:border-ink-700 hover:text-ink-900 transition-colors"
        >
          Back to home
        </Link>
      </section>
    );
  }

  if (reportsQ.isLoading) {
    return (
      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-12 text-ink-400">
        Loading reports…
      </section>
    );
  }

  const reports = reportsQ.data ?? [];
  const needle = search.trim().toLowerCase();
  const filtered = reports.filter((r) => {
    if (category && r.category !== category) return false;
    if (!needle) return true;
    const hay = [r.note ?? "", r.userEmail ?? "", String(r.exerciseId)]
      .join(" ")
      .toLowerCase();
    return hay.includes(needle);
  });

  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <PageEyebrow
        label="Admin"
        mono="reports"
        count={`${reports.length} ${reports.length === 1 ? "item" : "items"}`}
      />
      <h1 className="font-serif text-3xl text-ink-900 mb-2 tracking-tight">
        Reports
      </h1>
      <p className="text-ink-500 text-sm mb-5 sm:mb-6 max-w-xl">
        Learner-flagged content issues. Triage by opening the exercise and
        fixing in the seed file.
      </p>
      <div className="hairline mb-6 sm:mb-8" />
      {reports.length === 0 ? (
        <p className="text-ink-500">No reports yet — empty queue.</p>
      ) : (
        <>
          <FilterBar
            shown={filtered.length}
            total={reports.length}
            onClear={
              category || search
                ? () => {
                    setCategory("");
                    setSearch("");
                  }
                : undefined
            }
          >
            <FilterSelect
              layout="inline"
              label="Category"
              value={category}
              onChange={setCategory}
              options={Object.entries(CATEGORY_LABEL).map(([v, l]) => ({
                value: v,
                label: l,
              }))}
              placeholder="All categories"
            />
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="note, email, exercise id…"
              widthClass="w-64"
            />
          </FilterBar>

          {filtered.length === 0 && <FilterEmpty noun="reports" />}

          {filtered.length > 0 && (
            <ul className="divide-y divide-ink-100 border border-ink-100 bg-paper">
              {filtered.map((r) => (
                <li
                  key={r.id}
                  className="px-3 sm:px-4 py-3 hover:bg-ink-50 transition-colors"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <Link
                        to="/practice/$exerciseId"
                        params={{ exerciseId: String(r.exerciseId) }}
                        className="font-mono text-sm text-ink-700 hover:text-ink-900 underline underline-offset-2 decoration-1 transition-colors"
                      >
                        #{r.exerciseId}
                      </Link>
                      {/* Category is a citation, not a state — square mono. */}
                      <span className="inline-block font-mono text-[10px] uppercase tracking-wide text-ink-500 border border-ink-200 px-1.5 py-0.5">
                        {CATEGORY_LABEL[r.category] ?? r.category}
                      </span>
                    </div>
                    <div className="text-xs text-ink-400 tabular-nums">
                      {new Date(r.createdAt).toLocaleString()}
                    </div>
                  </div>
                  {r.note && (
                    <p className="text-sm text-ink-700 leading-snug mb-1">
                      {r.note}
                    </p>
                  )}
                  <p className="text-xs text-ink-400">
                    Reported by{" "}
                    <span className="text-ink-700">
                      {r.userEmail ?? `user #${r.userId}`}
                    </span>
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
